import { useState } from "react";
import { motion } from "framer-motion";
import { FileText, Download, ShieldCheck } from "lucide-react";
import DownloadGateModal from "@/components/DownloadGateModal";
import { useLang } from "@/i18n/LanguageContext";

/**
 * LegalStrip — Documents officiels + Identification légale
 * Section discrète mais élégante à insérer sur la page Manifeste (Home)
 * juste avant la Newsletter, pour transparence institutionnelle.
 */
export default function LegalStrip() {
  const { lang } = useLang();
  const en = lang === "en";
  const [dl, setDl] = useState({ open: false, doc: "statuts" });
  const openDl = (doc) => setDl({ open: true, doc });

  return (
    <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="legal-strip">
      <div className="border-t border-fg/15 pt-16 md:pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7 }}
          className="grid md:grid-cols-12 gap-10 md:gap-16 items-start"
        >
          <div className="md:col-span-4">
            <div className="meta-label text-cinabre">§ {en ? "Transparency" : "Transparence"}</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[0.98] tracking-tight">
              {en ? <>Trust, <em className="italic">in writing</em>.</> : <>La <em className="italic">confiance</em>, en toutes lettres.</>}
            </h2>
            <p className="mt-5 text-fg/70 leading-relaxed max-w-md">
              {en
                ? "An association registered with the prefecture and published in the Official Journal. Our statutes and JOAFE declaration are available upon simple identification."
                : "Association déclarée en préfecture, publiée au Journal Officiel. Nos statuts et notre déclaration JOAFE sont accessibles sur simple identification."}
            </p>
          </div>

          <div className="md:col-span-4">
            <div className="meta-label text-fg/60 mb-5">§ {en ? "Official documents" : "Documents officiels"}</div>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => openDl("statuts")}
                className="w-full flex items-center gap-4 p-4 border border-fg/15 hover:border-cinabre hover:bg-fg/[0.02] transition-all group text-left"
                data-testid="legal-dl-statuts"
              >
                <FileText size={18} className="shrink-0 text-cinabre" strokeWidth={1.4} />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg leading-tight">{en ? "Association statutes" : "Statuts de l'association"}</div>
                  <div className="mt-1 font-mono text-[9px] tracking-[0.22em] uppercase text-fg/50">PDF · {en ? "signed" : "signé"}</div>
                </div>
                <Download size={14} strokeWidth={1.4} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <button
                type="button"
                onClick={() => openDl("joafe")}
                className="w-full flex items-center gap-4 p-4 border border-fg/15 hover:border-cinabre hover:bg-fg/[0.02] transition-all group text-left"
                data-testid="legal-dl-joafe"
              >
                <FileText size={18} className="shrink-0 text-cinabre" strokeWidth={1.4} />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg leading-tight">Déclaration JOAFE</div>
                  <div className="mt-1 font-mono text-[9px] tracking-[0.22em] uppercase text-fg/50">PDF · Journal Officiel</div>
                </div>
                <Download size={14} strokeWidth={1.4} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
            <p className="mt-4 font-mono text-[9px] tracking-[0.22em] uppercase text-fg/45 flex items-center gap-2">
              <ShieldCheck size={12} strokeWidth={1.4} /> {en ? "Download after identification + email verification" : "Téléchargement sur identification préalable + vérification email"}
            </p>
          </div>

          <div className="md:col-span-4">
            <div className="meta-label text-fg/60 mb-5">§ {en ? "Legal identification" : "Identification légale"}</div>
            <dl className="space-y-4">
              <div className="flex items-baseline justify-between gap-4 pb-3 border-b border-fg/10">
                <dt className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/55">N° RNA</dt>
                <dd className="font-mono text-sm md:text-base tracking-[0.18em] text-fg">W313039555</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 pb-3 border-b border-fg/10">
                <dt className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/55">N° SIRET</dt>
                <dd className="font-mono text-sm md:text-base tracking-[0.18em] text-fg">992 816 124 00018</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <dt className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/55">{en ? "Head office" : "Siège social"}</dt>
                <dd className="font-serif text-sm md:text-base text-fg text-right leading-tight">40 Grande Rue<br/>31620 Castelnau d'Estrétefonds</dd>
              </div>
            </dl>
          </div>
        </motion.div>
      </div>

      <DownloadGateModal
        open={dl.open}
        onClose={() => setDl({ ...dl, open: false })}
        document={dl.doc}
      />
    </section>
  );
}
